import { create } from 'zustand';
import { apiService } from '@/services/api';
import { aggregateObservationScores, enrichObservationWithTrust } from '@/services/observationScoring';
import { enrichObservationWithMatch } from '@/utils/enrichObservationWithMatch';
import type { Observation } from '@/types';

/** Évite deux requêtes identiques (ex. React Strict Mode) pour le même match. */
const fetchByMatchInFlight = new Map<string, Promise<Observation[]>>();

interface ObservationState {
  all: Observation[];
  byMatch: Record<string, Observation[]>;
  isLoading: boolean;
  error: string | null;
  fromCache: boolean;
  fetchAll: (forceRefresh?: boolean) => Promise<Observation[]>;
  fetchByMatch: (matchId: string) => Promise<Observation[]>;
  getRecent: (limit?: number) => Observation[];
  getTop: (limit?: number) => Observation[];
  getByClubName: (clubName: string) => Observation[];
}

function toTime(obs: Observation): number {
  if (!obs.date) return 0;
  const t = new Date(obs.date).getTime();
  return Number.isFinite(t) ? t : 0;
}

function matchesClub(obs: Observation, clubName: string): boolean {
  const target = clubName.trim().toLowerCase();
  if (!target) return false;
  const candidates = [obs.teamName, obs.homeTeam, obs.awayTeam]
    .filter((v): v is string => Boolean(v))
    .map((v) => v.toLowerCase());
  return candidates.some((c) => c === target || c.includes(target) || target.includes(c));
}

function observationIdentityKey(obs: Observation): string {
  const first = (obs.prenom || '').trim().toLowerCase();
  const last = (obs.nom || '').trim().toLowerCase();
  const club = (obs.club || obs.teamName || '').trim().toLowerCase();
  const number = (obs.playerNumber || '').replace(/[^\d]/g, '');
  return [first, last, club, number].join('|');
}

export const useObservationStore = create<ObservationState>((set, get) => ({
  all: [],
  byMatch: {},
  isLoading: false,
  error: null,
  fromCache: false,

  fetchAll: async (forceRefresh = false) => {
    set({ isLoading: true, error: null });
    try {
      const result = await apiService.getObservations(forceRefresh);
      const enriched = result.data.map(enrichObservationWithTrust);
      set({ all: enriched, fromCache: result.fromCache, isLoading: false });
      return enriched;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Erreur de chargement des observations',
        isLoading: false,
      });
      return [];
    }
  },

  fetchByMatch: async (matchId: string) => {
    if (!matchId) return [];
    const key = String(matchId).trim();
    const existing = fetchByMatchInFlight.get(key);
    if (existing) return existing;

    const promise = (async (): Promise<Observation[]> => {
      set({ isLoading: true, error: null });
      try {
        const result = await apiService.getObservationsByMatch(key);
        const matchRes = await apiService.getMatchById(key);
        const enriched = result.data.map((obs) =>
          enrichObservationWithTrust(
            enrichObservationWithMatch(obs, matchRes?.data ?? null),
          ),
        );
        set((state) => ({
          byMatch: { ...state.byMatch, [key]: enriched },
          isLoading: false,
          fromCache: result.fromCache,
        }));
        return enriched;
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : 'Erreur de chargement des observations',
          isLoading: false,
        });
        return [];
      } finally {
        fetchByMatchInFlight.delete(key);
      }
    })();

    fetchByMatchInFlight.set(key, promise);
    return promise;
  },

  getRecent: (limit = 50) => {
    const { all } = get();
    return [...all].sort((a, b) => toTime(b) - toTime(a)).slice(0, limit);
  },

  getTop: (limit = 50) => {
    const { all } = get();
    const groups = new Map<string, Observation[]>();
    for (const obs of all) {
      const key = observationIdentityKey(obs);
      const existing = groups.get(key);
      if (existing) existing.push(obs);
      else groups.set(key, [obs]);
    }
    const scored = all.map((obs) => {
      const key = observationIdentityKey(obs);
      const group = groups.get(key) ?? [obs];
      const aggregate = aggregateObservationScores(group);
      return {
        ...obs,
        trustScore: obs.trustScore,
        robustRating: aggregate.robustRating,
        confidenceScore: aggregate.confidenceScore,
        confidenceLevel: aggregate.confidenceLevel,
        isProvisional: aggregate.isProvisional,
      } as Observation;
    });
    return scored.sort((a, b) => (b.robustRating ?? b.rating ?? 0) - (a.robustRating ?? a.rating ?? 0)).slice(0, limit);
  },

  getByClubName: (clubName: string) => {
    const { all } = get();
    if (!clubName) return [];
    return all.filter((o) => matchesClub(o, clubName));
  },
}));
